import { useSortable } from '@dnd-kit/sortable';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store/AppStore';
import {
  getFeatureType,
  getIconType,
  checkIsFeatureActive,
} from '../../../services/CategoryHelper';
import IconType from '../../../components/InputComponents/IconType';

import type { SortableItemProps } from '../../../../types';

/**
 * SortableItem component for displaying draggable and sortable items
 *
 * This component renders feature families or content items in a draggable/sortable format
 * using the dnd-kit library. It provides visual feedback during dragging operations and
 * supports item removal when in edit mode.
 *
 * @param {SortableItemProps} props - Component props
 * @returns {React.ReactElement} Rendered SortableItem component
 */
export const SortableItem: React.FC<SortableItemProps> = ({
  id,
  item,
  onRemove,
  isSortable,
  container,
  isEditMode,
}) => {
  const featureFamily = useSelector((state: AppState) => state.category.featureFamily);

  const type = getFeatureType(item, featureFamily);

  // Check if the feature is inactive
  const isInactive = !checkIsFeatureActive(item.code, featureFamily);

  /**
   * Data object used for dragging operations
   * Contains information about the item being dragged
   */
  const dragData = {
    container: container,
    itemType: 'familyType' in item ? item.familyType : (type ?? item.type),
    itemName: item.description,
    itemCode: item.code,
  };

  /**
   * Hook from dnd-kit that provides drag-and-drop functionality
   * Returns attributes and callbacks to enable dragging behavior
   */
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: dragData,
    disabled: !isEditMode || isInactive,
  });

  /**
   * Calculates the CSS classes for the item container
   * Classes are determined based on sortability, drag state, and edit mode
   */
  const itemClasses = [
    'draggable-sortable-item',
    isSortable ? 'sortable' : 'draggable',
    isDragging ? 'dragging' : '',
    isEditMode && !isInactive ? '' : 'disabled',
    isInactive ? 'inactive' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /**
   * Determines the inline style for the item
   * Applies visual feedback during dragging operations
   */
  const style = isDragging
    ? { opacity: 0.4 } // Lower opacity for original item during drag
    : {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
      };

  /**
   * Renders the appropriate icon for the feature item
   * @param {IFeatureFamily} item - The item to render an icon for
   * @returns {React.ReactElement} The rendered icon with tooltip
   */
  const featureIcon = (item: any) => {
    return (
      <Tooltip title={item?.familyType || type}>
        <span>
          <IconType type={getIconType({ ...item, type: type })} />
        </span>
      </Tooltip>
    );
  };

  return (
    <div
      ref={setNodeRef}
      {...(isEditMode && !isInactive ? listeners : {})}
      {...(isEditMode && !isInactive ? attributes : {})}
      className={itemClasses}
      title={item.description}
      style={style}
      data-item-code={item.code}
    >
      {featureIcon(item)}
      <div className="item-content">
        <span className={`item-description${isInactive ? ' strikethrough' : ''}`}>
          {item.description}
        </span>
        <span className={`item-code${isInactive ? ' strikethrough' : ''}`}>({item.code})</span>
      </div>
      {/* Only show actions for sortable items that are active */}
      {isEditMode && onRemove && !isInactive && (
        <div className="item-actions">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.code);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </div>
  );
};
